import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/shared/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { Button } from '@/shared/components/ui/button';
import { Flyout } from '@/shared/components/ui/flyout';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/shared/components/ui/alert-dialog';
import { EyeIcon, Trash } from 'lucide-react';
import { useEmbeddings } from '../hooks/useEmbeddings';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Badge } from '@/shared/components/ui/badge';
import { Search } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Filter } from 'lucide-react';
import { CategoryFilter, FilterState, ColumnState } from '@/shared/components/ui/category-filter';
import { X } from 'lucide-react';
import { DatePicker } from '@/shared/components/ui/date-picker';

interface KnowledgeEmbeddingsProps {
  knowledgeBaseId: string | undefined;
}

export function KnowledgeEmbeddings({ knowledgeBaseId }: KnowledgeEmbeddingsProps) {
  const { t } = useTranslation();
  const [openFlyout, setOpenFlyout] = useState(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedEmbedding, setSelectedEmbedding] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [embeddingToDelete, setEmbeddingToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDate, setSearchDate] = useState<Date | undefined>(undefined);
  const [searchFieldType, setSearchFieldType] = useState<'text' | 'date'>('text');
  const [filters, setFilters] = useState<FilterState>({});
  const [visibleColumns, setVisibleColumns] = useState<ColumnState>({
    id: true, // Set to true as this is the default search field in the backend
    inserted_at: false,
    metadata: false,
    version: false,
    actions: false
  });

  console.log('[KnowledgeEmbeddings] Rendering with knowledgeBaseId:', knowledgeBaseId);

  const effectiveSearchTerm = searchFieldType === 'text' 
    ? searchTerm 
    : (searchDate ? format(searchDate, 'yyyy-MM-dd') : '');
    
    
  const {
    embeddings: embeddingsHook,
    totalCount: totalCountHook,
    loading: loadingHook,
    page: pageHook,
    setPage: setPageHook,
    pageSize: pageSizeHook,
    setPageSize: setPageSizeHook,
    fetchEmbeddingDetails,
    deleteEmbedding
  } = useEmbeddings(
    knowledgeBaseId, 
    effectiveSearchTerm,
    visibleColumns
  );

  console.log('[KnowledgeEmbeddings] Embeddings:', embeddingsHook);
  console.log('[KnowledgeEmbeddings] Total count:', totalCountHook);
  console.log('[KnowledgeEmbeddings] Loading:', loadingHook);

  // Função interna para alternar o tipo de campo de busca
  // Agora usada apenas pelo componente CategoryFilter quando uma coluna de data é selecionada
  const setFieldType = (type: 'text' | 'date') => {
    console.log(`[KnowledgeEmbeddings] Setting field type to ${type.toUpperCase()}`);
    setSearchFieldType(type);
    
    if (type === 'date') {
      setSearchTerm('');
    } else {
      setSearchDate(undefined);
    }
  };
  
  const handlePageChange = (newPage: number) => {
    setPageHook(newPage);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSizeHook(parseInt(value));
    setPageHook(1); // Reset to first page when changing page size
  };

  const handleViewDetails = async (id: string) => {
    setLoadingDetails(true);
    const details = await fetchEmbeddingDetails(id);
    if (details) {
      setSelectedEmbedding(details);
      setOpenFlyout(true);
    }
    setLoadingDetails(false);
  };

  const handleCloseFlyout = () => {
    setOpenFlyout(false);
    setTimeout(() => setSelectedEmbedding(null), 300);
  };

  const handleDeleteClick = (id: string) => {
    setEmbeddingToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (embeddingToDelete) {
      await deleteEmbedding(embeddingToDelete);
      setDeleteDialogOpen(false);
      setEmbeddingToDelete(null);
    }
  };

  const totalPages = totalCountHook && pageSizeHook ? Math.ceil(totalCountHook / pageSizeHook) : 0;
  const startItem = pageHook && pageSizeHook ? (pageHook - 1) * pageSizeHook + 1 : 0;
  const endItem = pageHook && pageSizeHook && totalCountHook ? Math.min(pageHook * pageSizeHook, totalCountHook) : 0;

  const renderPagination = () => {
    if (!pageHook || !totalPages) return [];
    
    const pages = [];
    
    // Primeira página
    if (pageHook > 2) {
      pages.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>
            1
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Ellipsis no início
    if (pageHook > 3) {
      pages.push(
        <PaginationItem key="ellipsis-start">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Página anterior (se não for primeira)
    if (pageHook > 1) {
      pages.push(
        <PaginationItem key={pageHook - 1}>
          <PaginationLink onClick={() => handlePageChange(pageHook - 1)}>
            {pageHook - 1}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Página atual
    pages.push(
      <PaginationItem key={pageHook}>
        <PaginationLink isActive>
          {pageHook}
        </PaginationLink>
      </PaginationItem>
    );

    // Próxima página (se não for última)
    if (pageHook < totalPages) {
      pages.push(
        <PaginationItem key={pageHook + 1}>
          <PaginationLink onClick={() => handlePageChange(pageHook + 1)}>
            {pageHook + 1}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Ellipsis no final
    if (pageHook < totalPages - 2) {
      pages.push(
        <PaginationItem key="ellipsis-end">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }

    // Última página
    if (pageHook < totalPages - 1) {
      pages.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return pages;
  };

         // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatMetadata = (metadata: Record<string, any>) => {
    try {
      if (!metadata || Object.keys(metadata).length === 0) {
        return t('knowledge.common.none');
      }
      
      return Object.keys(metadata).map(key => (
        <Badge key={key} variant="outline" className="mr-1 mb-1">
          {key}: {typeof metadata[key] === 'object' ? '...' : String(metadata[key]).substring(0, 20)}
        </Badge>
      ));
    } catch (error) {
      return t('knowledge.common.error');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              {searchFieldType === 'text' ? (
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('knowledge.embeddings.search')}
                    className="pl-8 w-[250px]"
                  />
                  {searchTerm && (
                    <Button
                      variant="ghost"
                      className="absolute right-0 top-0 h-9 w-9 p-0"
                      onClick={() => setSearchTerm("")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ) : (
                <div className="relative w-[250px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                  <DatePicker
                    value={searchDate}
                    onChange={(date: Date | undefined) => setSearchDate(date)}
                    placeholder={t('knowledge.embeddings.search')}
                    width="250px"
                    className="pl-8 w-[250px]"
                  />
                </div>
              )}
              <CategoryFilter
                trigger={
                  <Button variant="outline" size="icon">
                    <Filter className="h-4 w-4" />
                  </Button>
                }
                columns={[
                  { id: 'id', name: t('knowledge.embeddings.fields.id'), type: 'text' },
                  { id: 'metadata', name: t('knowledge.embeddings.fields.metadata'), type: 'text' },
                  { id: 'version', name: t('knowledge.embeddings.fields.version'), type: 'text' },
                  { id: 'inserted_at', name: t('knowledge.embeddings.fields.insertedAt'), type: 'date' }
                ]}
                selectedColumns={visibleColumns}
                onColumnChange={(newColumns) => {
                  console.log('[KnowledgeEmbeddings] Column change detected:', JSON.stringify(newColumns));
                  
                  const wasDateColumnActive = visibleColumns['inserted_at'];
                  const isDateColumnActive = newColumns['inserted_at'];
                  
                  if (isDateColumnActive && !wasDateColumnActive) {
                    setFieldType('date');
                  } else if (!isDateColumnActive && wasDateColumnActive) {
                    setFieldType('text');
                  }
                  
                  setVisibleColumns(newColumns);
                }}
                selectedFilters={filters}
                onChange={(newFilters) => {
                  // Manter a funcionalidade de filtros para compatibilidade
                  setFilters(newFilters);
                }}
                onClear={() => {
                  setFilters({});
                  
                  setFieldType('text');
                  
                  setVisibleColumns({
                    id: true,
                    inserted_at: false,
                    metadata: false,
                    version: false,
                    actions: false
                  });
                }}
              />
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px]">{t('knowledge.embeddings.fields.id')}</TableHead>
                  <TableHead>{t('knowledge.embeddings.fields.insertedAt')}</TableHead>
                  <TableHead>{t('knowledge.embeddings.fields.metadata')}</TableHead>
                  <TableHead className="text-center">{t('knowledge.embeddings.fields.version')}</TableHead>
                  <TableHead className="text-center">{t('knowledge.embeddings.fields.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingHook ? (
                  Array.from({ length: pageSizeHook }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[50px]" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    </TableRow>
                  ))
                ) : embeddingsHook && embeddingsHook.length > 0 ? (
                  // Renderizar os dados diretamente, sem filtros no lado do cliente
                  // Todo o filtro é feito no backend
                  embeddingsHook.map((embedding) => (
                    <TableRow key={embedding.id}>
                      <TableCell className="font-mono text-xs">
                        {embedding.id.substring(0, 12)}...
                      </TableCell>
                      <TableCell>
                        {embedding.inserted_at ? format(new Date(embedding.inserted_at), 'dd/MM/yyyy HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap max-w-[300px]">
                          {formatMetadata(embedding.metadata)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{embedding.version}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewDetails(embedding.id)}
                            disabled={loadingDetails}
                            title={t('knowledge.embeddings.view_details')}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(embedding.id)}
                            className="text-red-600 hover:text-red-700"
                            title={t('knowledge.common.delete')}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      {t('knowledge.embeddings.noEmbeddings')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {loadingHook ? (
                <Skeleton className="h-4 w-[250px]" />
              ) : (
                totalCountHook && totalCountHook > 0 ? (
                  t('knowledge.embeddings.showing', { start: startItem, end: endItem, total: totalCountHook })
                ) : (
                  t('knowledge.embeddings.noResults')
                )
              )}
            </div>
            
            <div className="flex-1 flex justify-center items-center">
              {totalPages && totalPages > 1 && (
                <Pagination className="mx-auto">
                  <PaginationContent className="flex justify-center">
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => handlePageChange(Math.max(1, pageHook - 1))}
                        aria-disabled={pageHook === 1}
                        className={pageHook === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    
                    {renderPagination()}
                    
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => handlePageChange(Math.min(totalPages, pageHook + 1))}
                        aria-disabled={pageHook === totalPages}
                        className={pageHook === totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>

            <div className="flex items-center gap-2 justify-end flex-1">
              <span className="text-sm text-muted-foreground">{t('knowledge.embeddings.itemsPerPage')}</span>
              <Select
                value={pageSizeHook.toString()}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder={pageSizeHook.toString()} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

        </div>
      </Card>

      <Flyout 
        open={openFlyout} 
        onClose={handleCloseFlyout}
      >
        {selectedEmbedding ? (
          <div className="p-6 w-full max-w-3xl">
            <h2 className="text-2xl font-bold mb-4">{t('knowledge.embeddings.details')}</h2>
            
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>{t('knowledge.embeddings.basicInfo')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">{t('knowledge.embeddings.fields.id')}</dt>
                      <dd className="font-mono text-sm">{selectedEmbedding.id}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">{t('knowledge.embeddings.fields.version')}</dt>
                      <dd>{selectedEmbedding.version}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">{t('knowledge.embeddings.fields.insertedAt')}</dt>
                      <dd>{format(new Date(selectedEmbedding.inserted_at), 'dd/MM/yyyy HH:mm:ss')}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-muted-foreground">{t('knowledge.embeddings.fields.knowledgeId')}</dt>
                      <dd className="font-mono text-sm">{selectedEmbedding.knowledge_id}</dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>{t('knowledge.embeddings.fields.content')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px] w-full rounded-md border p-4">
                    <pre className="text-sm whitespace-pre-wrap">{selectedEmbedding.content}</pre>
                  </ScrollArea>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>{t('knowledge.embeddings.fields.metadata')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[150px] w-full rounded-md border p-4">
                    <pre className="text-sm whitespace-pre-wrap">
                      {JSON.stringify(selectedEmbedding.metadata, null, 2)}
                    </pre>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="p-6 flex justify-center items-center">
            <Skeleton className="h-[400px] w-full max-w-3xl" />
          </div>
        )}
      </Flyout>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('knowledge.embeddings.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('knowledge.embeddings.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>{t('knowledge.common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
